import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Download, Search, Trash2 } from "lucide-react";
import { api, errText } from "@/lib/api";
import { useCustomer } from "@/lib/customer";
import { fmtDate, fmtMoney, fmtNumber, STAGES } from "@/lib/format";
import { LdBadge, Mono } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function AllPos() {
  const { customerId, onExpiryClock } = useCustomer();
  const [params] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ people: [], states: [], stages: STAGES });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: "",
    owner: "",
    stage: "",
    ldColour: params.get("ldColour") || "",
    state: "",
    status: params.get("status") || "open",
    fromDate: "",
    toDate: "",
  });
  const navigate = useNavigate();
  const { isManager } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    const query = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== "" && v !== null));
    try {
      const { data } = await api.get("/polines", { params: query });
      setRows(data.rows);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load, customerId]); // RELOAD_ON_CUSTOMER

  useEffect(() => {
    api.get("/meta").then(({ data }) => setMeta(data)).catch(() => {});
  }, []);

  const exportCsv = async () => {
    try {
      const response = await api.post(
        "/reports/export",
        {
          columns: ["poNumber", "poDate", "branchCode", "branchName", "city", "state",
            "itemType", "product", "qty", "rate", "lineValue", "owner", "stageLabel", "ldStatus", "ageing",
            "deliveryDue", "completionDue", "nextAction"],
          filters,
        },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "kanex-po-lines.csv";
      link.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <div className="space-y-4" data-testid="all-pos-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">All POs</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            <Mono>{fmtNumber(rows.length)}</Mono> item line(s) match the filters.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={exportCsv} data-testid="all-pos-export-button">
          <Download className="mr-2 h-4 w-4" />
          Export CSV
        </Button>
      </div>

      <div className="panel grid gap-3 p-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="sm:col-span-2">
          <Label className="field-label">Search</Label>
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              value={filters.search}
              onChange={(e) => set("search", e.target.value)}
              placeholder="PO, SO, branch, city, bill…"
              className="pl-8"
              data-testid="all-pos-search-input"
            />
          </div>
        </div>
        {[
          ["owner", "Owner", [["", "Everyone"], ["Unassigned", "Unassigned"], ...meta.people.map((p) => [p, p])]],
          ["stage", "Stage", [["", "Any stage"], ...(meta.stages || STAGES).map((s) => [String(s.value), s.label])]],
          ["ldColour", onExpiryClock ? "Expiry" : "LD status", [["", "Any"], ["red", onExpiryClock ? "Past expiry" : "LD running"], ["amber", onExpiryClock ? "Near expiry" : "At risk"], ["green", "On time"], ["grey", "Closed"]]],
          ["state", "State", [["", "Any state"], ...meta.states.map((s) => [s, s])]],
          ["status", "Open or closed", [["", "All"], ["open", "Open"], ["closed", "Closed"]]],
        ].map(([key, label, options]) => (
          <div key={key}>
            <Label className="field-label">{label}</Label>
            <select
              value={filters[key]}
              onChange={(e) => set(key, e.target.value)}
              className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
              data-testid={`all-pos-${key}-filter`}
            >
              {options.map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>
        ))}
        <div>
          <Label className="field-label">PO date from</Label>
          <Input type="date" value={filters.fromDate} onChange={(e) => set("fromDate", e.target.value)} className="font-mono" data-testid="all-pos-from-filter" />
        </div>
        <div>
          <Label className="field-label">PO date to</Label>
          <Input type="date" value={filters.toDate} onChange={(e) => set("toDate", e.target.value)} className="font-mono" data-testid="all-pos-to-filter" />
        </div>
      </div>

      <div className="panel overflow-x-auto">
        <table className="w-full min-w-[980px] text-left">
          <thead className="sticky top-0 border-b border-border bg-secondary/60 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              {["PO number", "PO date", "Branch", "Item", "Qty", "Line value", "Owner", "Stage", onExpiryClock ? "Expiry" : "LD", "Delivery due", "Ageing"].map((h) => (
                <th key={h} className="cell font-medium">{h}</th>
              ))}
              {isManager && <th className="cell font-medium"> </th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((row) => (
              <tr
                key={row.id}
                onClick={() => navigate(`/po-history/${row.id}`)}
                className="cursor-pointer hover:bg-secondary/50"
                data-testid={`all-pos-row-${row.id}`}
              >
                <td className="cell font-mono">{row.poNumber}</td>
                <td className="cell font-mono">{fmtDate(row.poDate)}</td>
                <td className="cell">
                  {row.branchName || "—"}
                  <span className="ml-1 font-mono text-[11px] text-muted-foreground">{row.branchCode || ""}</span>
                </td>
                <td className="cell">{row.itemType || "—"}</td>
                <td className="cell font-mono">{fmtNumber(row.qty)}</td>
                <td className="cell font-mono">{fmtMoney(row.derived.lineValue)}</td>
                <td className="cell">{row.owner || "Unassigned"}</td>
                <td className="cell">{row.derived.stageLabel}</td>
                <td className="cell">
                  <LdBadge colour={row.derived.ldColour} text={row.derived.ldStatus} testId={`all-pos-ld-${row.id}`} />
                </td>
                <td className="cell font-mono">{fmtDate(row.derived.deliveryDue)}</td>
                <td className="cell font-mono">{row.derived.ageing}d</td>
                {isManager && (
                  <td className="cell">
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!window.confirm(`Delete the whole PO ${row.poNumber} (${row.branchName || "—"})? It is hidden, not destroyed.`)) return;
                        try {
                          const { data: res } = await api.delete(`/po/${row.id}`);
                          toast.success(`PO deleted — ${res.deleted} line(s) hidden`);
                          await load();
                        } catch (err) {
                          toast.error(errText(err));
                        }
                      }}
                      className="text-muted-foreground hover:text-red-600"
                      data-testid={`all-pos-delete-${row.id}`}
                      aria-label="Delete this PO"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={isManager ? 12 : 11} className="p-8 text-center text-sm text-muted-foreground" data-testid="all-pos-empty">
                  No lines match these filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
